import { useCallback } from 'react';

import { sessionManager } from './sessionManager';

/**
 * The shape of the data required to start a new session.
 */
export interface SessionData {
  app_id: number;
  email: string;
  device: string;
}

/**
 * A React hook that provides session lifecycle actions:
 * - startSession: begins a session with required metadata
 * - updateSession: updates last activity timestamp
 * - endSession: gracefully closes a session
 *
 * This hook abstracts sessionManager so components only call these
 * methods without worrying about implementation details.
 */
export const useSessionManager = () => {
  /**
   * Starts a new user session.
   * @param data - Session details (app_id, email, device)
   */
  const startSession = useCallback(async (data: SessionData) => {
    await sessionManager.start(data);
  }, []);

  /**
   * Updates the active session (keeps it alive).
   */
  const updateSession = useCallback(async () => {
    await sessionManager.update();
  }, []);

  /**
   * Ends the active session.
   */
  const endSession = useCallback(async () => {
    await sessionManager.end();
  }, []);

  return { startSession, updateSession, endSession };
};
